import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  booleanAttribute,
  computed,
  contentChildren,
  effect,
  input,
  model,
  output,
  viewChildren,
} from '@angular/core';
import {
  Tab as AriaTab,
  TabContent as AriaTabContent,
  TabList as AriaTabList,
  TabPanel as AriaTabPanel,
  Tabs as AriaTabs,
} from '@angular/aria/tabs';
import { FcTabChangeEvent } from './tab-change-event';
import { FcTab } from './tab.component';

@Component({
  selector: 'fc-tab-group',
  template: `
    <div
      class="flex min-w-0 flex-col"
      [class.h-full]="fillHeight()"
      [class.min-h-0]="fillHeight()"
      ngTabs
    >
      <div
        class="fc-tab-header min-w-0 border-secondary-100"
        [class.border-b]="showBottomBorder()"
        [style.padding-inline.px]="headerPadding()"
      >
        <div
          class="fc-tab-list flex min-w-0 overflow-x-auto overflow-y-hidden"
          ngTabList
          orientation="horizontal"
          selectionMode="follow"
          focusMode="roving"
          [selectedTab]="selectedTabValue()"
          (selectedTabChange)="onSelectedTabValueChange($event)"
          [class.justify-center]="alignTabs() === 'center'"
          [class.justify-end]="alignTabs() === 'end'"
        >
          @for (tab of tabs(); track tab.value; let index = $index) {
            <button
              #tabButton
              type="button"
              ngTab
              [value]="tab.value"
              [disabled]="tab.disabled()"
              class="fc-tab-label relative inline-flex h-40 min-w-52 flex-none items-center justify-center gap-8 whitespace-nowrap border-0 border-b-2 border-solid bg-transparent px-16 text-base leading-24 font-medium max-md:h-36 max-md:px-12 max-md:text-sm max-md:leading-20 transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-300 cursor-pointer hover:bg-secondary-50 active:bg-secondary-100 aria-disabled:cursor-not-allowed aria-disabled:hover:bg-transparent aria-disabled:active:bg-transparent"
              [style.flex]="stretchTabs() ? '1 0 auto' : null"
              [class.text-secondary-300]="tab.disabled()"
              [class.opacity-50]="tab.disabled()"
              [class.text-primary-600]="
                !tab.disabled() && selectedIndex() === index
              "
              [class.border-b-primary-600]="
                !tab.disabled() && selectedIndex() === index
              "
              [class.text-secondary-400]="
                !tab.disabled() && selectedIndex() !== index
              "
              [class.border-b-transparent]="
                tab.disabled() || selectedIndex() !== index
              "
            >
              @if (tab.labelTemplate(); as labelTemplate) {
                <ng-container [ngTemplateOutlet]="labelTemplate"></ng-container>
              } @else {
                <span>{{ tab.textLabel() }}</span>
              }
            </button>
          }
        </div>
      </div>

      <div
        class="min-w-0"
        [class.flex-1]="fillHeight()"
        [class.min-h-0]="fillHeight()"
      >
        @for (tab of tabs(); track tab.value; let index = $index) {
          <div
            ngTabPanel
            [value]="tab.value"
            [preserveContent]="true"
            [class.hidden]="selectedIndex() !== index"
            [class.h-full]="fillHeight()"
            [class.overflow-y-auto]="fillHeight()"
          >
            <ng-template ngTabContent>
              @if (tab.contentTemplate(); as contentTemplate) {
                <ng-container
                  [ngTemplateOutlet]="contentTemplate"
                ></ng-container>
              }
            </ng-template>
          </div>
        }
      </div>
    </div>

    <div class="hidden" aria-hidden="true">
      <ng-content select="fc-tab"></ng-content>
    </div>
  `,
  styles: `
    :host {
      display: block;
      min-width: 0;
    }

    :host(.fc-tabs-fill-height) {
      display: flex;
      flex-direction: column;
      min-height: 0;
      flex: 1;
    }

    .fc-tab-list {
      scrollbar-width: none;
    }

    .fc-tab-list::-webkit-scrollbar {
      display: none;
    }
  `,
  imports: [
    NgTemplateOutlet,
    AriaTabs,
    AriaTabList,
    AriaTab,
    AriaTabPanel,
    AriaTabContent,
  ],
  host: {
    '[class.fc-tabs-fill-height]': 'fillHeight()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FcTabGroup {
  readonly tabs = contentChildren(FcTab);
  readonly selectedIndex = model(0);

  readonly stretchTabs = input(false, {
    alias: 'fc-stretch-tabs',
    transform: booleanAttribute,
  });
  readonly alignTabs = input<string | null>(null, { alias: 'fc-align-tabs' });
  readonly showBottomBorder = input(true, {
    alias: 'fc-show-bottom-border',
    transform: booleanAttribute,
  });
  readonly fillHeight = input(false, {
    alias: 'fc-fill-height',
    transform: booleanAttribute,
  });
  readonly headerPadding = input<number | null>(null, {
    alias: 'fc-header-padding',
  });
  readonly animationDuration = input<string | number | null>(null);
  readonly color = input<string | null>(null);

  readonly selectedTabChange = output<FcTabChangeEvent>();

  private readonly tabButtons =
    viewChildren<ElementRef<HTMLElement>>('tabButton');

  readonly selectedTabValue = computed(
    () => this.tabs()[this.selectedIndex()]?.value,
  );

  constructor() {
    // Keep the selected index within range and off disabled tabs.
    effect(() => {
      const tabs = this.tabs();
      if (!tabs.length) return;

      const current = this.selectedIndex();
      const clamped = Math.min(Math.max(current, 0), tabs.length - 1);
      if (tabs[clamped] && !tabs[clamped].disabled()) {
        if (clamped !== current) this.selectedIndex.set(clamped);
        return;
      }

      const firstEnabled = tabs.findIndex((tab) => !tab.disabled());
      this.selectedIndex.set(firstEnabled >= 0 ? firstEnabled : 0);
    });

    // Keep the active tab scrolled into view when selection or tabs change.
    effect(() => {
      this.selectedIndex();
      this.tabButtons();
      queueMicrotask(() => this.scrollSelectedTabIntoView());
    });
  }

  onSelectedTabValueChange(value: string | undefined): void {
    const tabs = this.tabs();
    const index = tabs.findIndex((tab) => tab.value === value);
    const tab = tabs[index];
    if (!tab || tab.disabled()) return;
    if (index === this.selectedIndex()) return;

    this.selectedIndex.set(index);
    this.selectedTabChange.emit({ index, tab });
  }

  private scrollSelectedTabIntoView(): void {
    const button = this.tabButtons()[this.selectedIndex()]?.nativeElement;
    const tabList = button?.parentElement;
    if (!button || !tabList) return;
    const buttonRect = button.getBoundingClientRect();
    const tabListRect = tabList.getBoundingClientRect();
    if (buttonRect.left < tabListRect.left) {
      tabList.scrollLeft -= tabListRect.left - buttonRect.left;
    } else if (buttonRect.right > tabListRect.right) {
      tabList.scrollLeft += buttonRect.right - tabListRect.right;
    }
  }
}
